import React, { useEffect, useMemo, useState } from 'react';
import { API } from '../config/api';
import { getAuthToken } from '../utils/auth';

const PRIORITY_OPTIONS = ['critical', 'high', 'medium', 'low'];
const STATUS_OPTIONS = ['active', 'paused', 'completed', 'archived'];
const CATEGORY_OPTIONS = ['business', 'health', 'finance', 'learning', 'personal', 'general'];

const priorityColor = (p) => ({
  critical: 'text-rose-300 border-rose-500/30 bg-rose-500/10',
  high: 'text-amber-300 border-amber-500/30 bg-amber-500/10',
  medium: 'text-zinc-300 border-zinc-500/30 bg-zinc-500/10',
  low: 'text-zinc-500 border-zinc-700 bg-zinc-900',
}[p] || 'text-zinc-400 border-zinc-700');

const statusColor = (s) => ({
  active: 'text-emerald-300',
  paused: 'text-amber-400',
  completed: 'text-sky-300',
  archived: 'text-zinc-500',
}[s] || 'text-zinc-400');

async function api(path, { method = 'GET', body, signal } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthToken()}`,
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text}`);
  }
  return res.json();
}

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({
    title: '',
    description: '',
    category: 'business',
    priority: 'high',
    next_step: '',
    deadline: '',
  });
  const [saving, setSaving] = useState(false);

  const [focusBrief, setFocusBrief] = useState(null);
  const [focusLoading, setFocusLoading] = useState(false);

  const [suggestionByGoal, setSuggestionByGoal] = useState({});
  const [suggestLoading, setSuggestLoading] = useState({});

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const q = filter && filter !== 'all' ? `?status=${filter}` : '';
      const data = await api(`/goals${q}`);
      setGoals(data.goals || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    setSaving(true);
    setError('');
    try {
      await api('/goals', {
        method: 'POST',
        body: {
          ...draft,
          deadline: draft.deadline || null,
        },
      });
      setDraft({
        title: '',
        description: '',
        category: 'business',
        priority: 'high',
        next_step: '',
        deadline: '',
      });
      setShowCreate(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const updateGoal = async (id, changes) => {
    try {
      await api(`/goals/${id}`, { method: 'PATCH', body: changes });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const archiveGoal = async (id) => {
    try {
      await api(`/goals/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const loadFocus = async () => {
    setFocusLoading(true);
    try {
      const data = await api('/goals/focus');
      setFocusBrief(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setFocusLoading(false);
    }
  };

  const suggestNext = async (goalId) => {
    setSuggestLoading((s) => ({ ...s, [goalId]: true }));
    try {
      const data = await api(`/goals/${goalId}/suggest-next-step`, { method: 'POST' });
      setSuggestionByGoal((s) => ({ ...s, [goalId]: data.suggestion }));
    } catch (e) {
      setSuggestionByGoal((s) => ({ ...s, [goalId]: `(error: ${e.message})` }));
    } finally {
      setSuggestLoading((s) => ({ ...s, [goalId]: false }));
    }
  };

  const sortedGoals = useMemo(() => {
    const rank = { critical: 0, high: 1, medium: 2, low: 3 };
    return [...goals].sort((a, b) => {
      const r = (rank[a.priority] ?? 9) - (rank[b.priority] ?? 9);
      if (r !== 0) return r;
      return (a.deadline || 'z').localeCompare(b.deadline || 'z');
    });
  }, [goals]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="goals-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <header className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-amber-500/80 mb-2">
              Goal Memory
            </div>
            <h1 className="text-3xl font-semibold tracking-tight">What I'm working on</h1>
            <p className="text-zinc-400 mt-2 text-sm">
              Long-term goals LEE remembers between conversations. These never auto-fill from chat.
            </p>
          </div>
          <button
            data-testid="new-goal-btn"
            onClick={() => setShowCreate((v) => !v)}
            className="px-4 py-2 rounded-md bg-amber-500 text-black text-sm font-medium hover:bg-amber-400 transition"
          >
            {showCreate ? 'Cancel' : 'New goal'}
          </button>
        </header>

        {error && (
          <div className="mb-6 px-4 py-3 rounded border border-rose-500/30 bg-rose-500/5 text-rose-300 text-sm" data-testid="goals-error">
            {error}
          </div>
        )}

        {/* Focus brief */}
        <section className="mb-10 border border-zinc-800 rounded-lg p-5 bg-zinc-900/40">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm uppercase tracking-widest text-zinc-400">Focus brief</div>
            <button
              data-testid="focus-btn"
              onClick={loadFocus}
              disabled={focusLoading}
              className="text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50"
            >
              {focusLoading ? 'Thinking…' : focusBrief ? 'Refresh' : 'Ask LEE what to focus on'}
            </button>
          </div>
          {focusBrief ? (
            <div className="text-zinc-200 leading-relaxed whitespace-pre-wrap" data-testid="focus-summary">
              {focusBrief.summary}
            </div>
          ) : (
            <div className="text-zinc-500 text-sm">
              Press the button to get a short, natural brief on what to prioritize right now.
            </div>
          )}
        </section>

        {/* Create form */}
        {showCreate && (
          <form
            onSubmit={handleCreate}
            className="mb-10 border border-zinc-800 rounded-lg p-5 bg-zinc-900/40 space-y-3"
            data-testid="create-goal-form"
          >
            <input
              data-testid="goal-title"
              required
              autoFocus
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="What's the goal?"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-zinc-100 focus:outline-none focus:border-amber-500"
            />
            <textarea
              data-testid="goal-description"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Why does this matter? (optional)"
              rows={2}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-zinc-100 text-sm focus:outline-none focus:border-amber-500"
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <select
                data-testid="goal-category"
                value={draft.category}
                onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
              >
                {CATEGORY_OPTIONS.map((c) => <option key={c}>{c}</option>)}
              </select>
              <select
                data-testid="goal-priority"
                value={draft.priority}
                onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
                className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
              >
                {PRIORITY_OPTIONS.map((p) => <option key={p}>{p}</option>)}
              </select>
              <input
                data-testid="goal-deadline"
                type="date"
                value={draft.deadline}
                onChange={(e) => setDraft({ ...draft, deadline: e.target.value })}
                className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-300"
              />
              <input
                data-testid="goal-next-step"
                value={draft.next_step}
                onChange={(e) => setDraft({ ...draft, next_step: e.target.value })}
                placeholder="Next step (optional)"
                className="bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-200"
              >
                Cancel
              </button>
              <button
                data-testid="save-goal-btn"
                type="submit"
                disabled={saving || !draft.title.trim()}
                className="px-4 py-1.5 rounded bg-amber-500 text-black text-sm font-medium hover:bg-amber-400 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save goal'}
              </button>
            </div>
          </form>
        )}

        {/* Filter */}
        <div className="flex items-center gap-2 mb-5 text-sm" data-testid="goals-filters">
          {['active', 'paused', 'completed', 'archived', 'all'].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-3 py-1 rounded-full border ${
                filter === s
                  ? 'border-amber-500/60 text-amber-300 bg-amber-500/10'
                  : 'border-zinc-800 text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {s}
            </button>
          ))}
          <span className="ml-auto text-xs text-zinc-500" data-testid="goals-count">
            {loading ? 'loading…' : `${sortedGoals.length} ${filter}`}
          </span>
        </div>

        {/* Goals list */}
        <ul className="space-y-3" data-testid="goals-list">
          {!loading && sortedGoals.length === 0 && (
            <li className="text-zinc-500 text-sm py-12 text-center border border-dashed border-zinc-800 rounded-lg">
              No goals here yet. Click <span className="text-amber-400">New goal</span> to add one.
            </li>
          )}
          {sortedGoals.map((g) => (
            <li
              key={g.id}
              data-testid={`goal-${g.id}`}
              className="border border-zinc-800 rounded-lg p-4 bg-zinc-900/30 hover:border-zinc-700 transition"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${priorityColor(g.priority)}`}>
                      {g.priority}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">
                      {g.category}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider ${statusColor(g.status)}`}>
                      · {g.status}
                    </span>
                    {g.deadline && (
                      <span className="text-[10px] text-zinc-500">· due {g.deadline}</span>
                    )}
                  </div>
                  <div className="text-zinc-100 font-medium">{g.title}</div>
                  {g.description && (
                    <div className="text-sm text-zinc-400 mt-1">{g.description}</div>
                  )}
                  {g.next_step && (
                    <div className="text-sm text-amber-300/90 mt-2">
                      <span className="text-zinc-500">next:</span> {g.next_step}
                    </div>
                  )}
                  {suggestionByGoal[g.id] && (
                    <div className="mt-3 px-3 py-2 rounded border border-amber-500/20 bg-amber-500/5 text-sm text-amber-200" data-testid={`suggestion-${g.id}`}>
                      <span className="text-amber-500/70 text-xs uppercase tracking-wider mr-2">LEE suggests</span>
                      {suggestionByGoal[g.id]}
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button
                    data-testid={`suggest-${g.id}`}
                    onClick={() => suggestNext(g.id)}
                    disabled={suggestLoading[g.id]}
                    className="text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50"
                  >
                    {suggestLoading[g.id] ? 'Thinking…' : 'Suggest next step'}
                  </button>
                  <select
                    data-testid={`status-${g.id}`}
                    value={g.status}
                    onChange={(e) => updateGoal(g.id, { status: e.target.value })}
                    className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-300"
                  >
                    {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  {g.status !== 'archived' && (
                    <button
                      data-testid={`archive-${g.id}`}
                      onClick={() => archiveGoal(g.id)}
                      className="text-xs text-zinc-500 hover:text-rose-400"
                    >
                      Archive
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
